import type { ListBuilderConfiguration } from '../types';

/**
 * Conversation Analyzer list configuration
 * Shows AI performance analyzers in the system
 */
export const CONVERSATION_ANALYZER_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-analyzers-all',
			cacheTTL: 600000,
			name: 'All Analyzers',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'analyzer-info',
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: '_conversation_analyzer',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: '_conversation_analyzer',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'description',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: '_conversation_analyzer',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Analyzer' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: '_conversation_analyzer',
						},
					],
					verticalAlignment: 'top',
				},
				{
					id: 'llm-config',
					fields: [
						{
							id: 'llm-provider',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'llm_provider',
									objectTable: '_conversation_analyzer',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'value-mapping',
									valueMapping: [
										{
											value: 'claude',
										},
										{
											value: 'openai',
										},
									],
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'llm-model',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'llm_model',
									objectTable: '_conversation_analyzer',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'LLM' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'criteria-count',
					fields: [
						{
							id: 'criteria-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'criteriaCount',
									objectTable: '_conversation_analyzer',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'criteria-label',
							dataConfig: {
								type: 'custom_text',
								customText: 'criteria',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h3', text: 'Criteria' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', width: 80 },
					verticalAlignment: 'middle',
				},
				{
					id: 'status',
					fields: [
						{
							id: 'status-badge',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'is_active',
									objectTable: '_conversation_analyzer',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'value-mapping',
									valueMapping: [
										{
											value: 'true',
											label: 'Active',
											color: { type: 'success' },
										},
										{
											value: 'false',
											label: 'Inactive',
											color: { type: 'neutral' },
										},
									],
								},
							},
							displayStyle: 'status_indicator',
						},
					],
					headers: [{ id: 'h4', text: 'Status' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'is_active',
							objectTable: '_conversation_analyzer',
						},
					],
					widthConfig: { mode: 'fixed', width: 100 },
					verticalAlignment: 'middle',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'view-action',
									label: 'View',
									iconName: 'eye',
									callbackName: 'viewAnalyzer',
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editAnalyzer',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteAnalyzer',
									variant: 'danger',
								},
							],
						},
					],
					headers: [{ id: 'h5', text: 'Actions' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', width: 80 },
					verticalAlignment: 'middle',
				},
			],
			description: 'All AI conversation performance analyzers',
			defaultSortId: 'column-analyzer-info-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['analyzerSearch', 'analyzerStatus', 'analyzerProvider'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/ai-agents/analyzers/:id',
			objectLabel: 'Analyzer',
			objectTable: '_conversation_analyzer',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'is_active', fieldLabel: 'Active' },
				{ fieldName: 'llm_provider', fieldLabel: 'LLM Provider' },
				{ fieldName: 'llm_model', fieldLabel: 'LLM Model' },
				{ fieldName: 'config', fieldLabel: 'Config' },
				{ fieldName: '_created_date', fieldLabel: 'Created' },
				{ fieldName: '_modified_date', fieldLabel: 'Modified' },
				{ fieldName: 'criteriaCount', fieldLabel: 'Criteria Count' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: '_conversation_analyzer',
				},
			],
			description: 'Sort by name',
		},
		{
			id: 'created',
			name: 'Created Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: '_created_date',
					objectTable: '_conversation_analyzer',
				},
			],
			description: 'Sort by creation date',
		},
		{
			id: 'modified',
			name: 'Last Modified',
			fields: [
				{
					direction: 'DESC',
					fieldName: '_modified_date',
					objectTable: '_conversation_analyzer',
				},
			],
			description: 'Sort by last modified date',
		},
	],
};
